import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService }                               from '@ngx-translate/core';
import { UIRouter }                                       from '@uirouter/core';

import { CompanyService }              from '../companies/company.service';
import { MessagesService }             from '../messages/messages.service';
import { RemoveComponent }             from '../table/cell-components/actions/remove.component';
import { Table }                       from '../table/types/table';
import { TableSwitchControlComponent } from '../table/cell-components/controls/switch-control/table-switch-control.component';
import { TableTextControlComponent }   from '../table/cell-components/controls/text-control/table-text-control.component';
import { TaxGroupActionsComponent }    from './tax-group.actions.component';
import { TaxGroupItemAttributeModel }  from './typings/tax-group.item-attributes';
import { TaxGroupModel }               from './typings/tax-group';
import { TaxGroupsService }            from './tax-groups.service';
import { XeroTaxRateModel }            from '../xero/models/xero.tax-rate';

@Component({
  selector   : 'wor-tax-group',
  template: require('./tax-group.component.html')
})
export class TaxGroupComponent implements OnInit {
  @Input() group             : TaxGroupModel;
  @Input() index             : number;
  @Input() quickbooksTaxRates: Array<any> = [];
  @Input() xeroTaxRates      : Array<XeroTaxRateModel> = [];

  @Output() onDelete : EventEmitter<any> = new EventEmitter();

  broadcastKey : string;
  originalTypes: Map<string, TaxGroupItemAttributeModel> = new Map();
  tableSettings: Table;

  constructor (
    public  companyService  : CompanyService,
    private messagesService : MessagesService,
    private router          : UIRouter,
    private taxGroupsService: TaxGroupsService,
    private translateService: TranslateService
  ) {}

  ngOnInit () : void {
    this.broadcastKey = `filterTaxGroup${ this.index }`;

    this.tableSettings = {
      broadcast: this.broadcastKey,
      columns  : [
        {
          component: {
            bindings: {
              outputs: {
                onRemoveClick: ( type : TaxGroupItemAttributeModel ) => this.deleteType(type)
              }
            },
            enabled: true,
            factory: RemoveComponent,
          },
          sortable: false,
        },
        {
          component: {
            bindings: {
              inputs: {
                disabled: ( type : TaxGroupItemAttributeModel ) => !type.editing,
                model   : 'tax_type',
                name    : `tax_type_${ this.index }`
              }
            },
            enabled: true,
            factory: TableTextControlComponent
          },
          field : 'tax_type',
          header: {
            text: this.translateService.instant('COMPANY_SPACE.TAX_GROUP.TAX_TYPE')
          }
        },
        {
          component: {
            bindings: {
              inputs: {
                disabled: ( type : TaxGroupItemAttributeModel ) => !type.editing,
                model   : 'tax_rate',
                name    : `tax_rate_${ this.index }`,
                type    : 'number'
              }
            },
            enabled: true,
            factory: TableTextControlComponent
          },
          field : 'tax_rate',
          header: {
            text: this.translateService.instant('COMPANY_SPACE.TAX_GROUP.RATE')
          },
          pipe: 'number',
        },
        {
          component: {
            bindings: {
              inputs: {
                disabled: ( type : TaxGroupItemAttributeModel ) => !type.editing,
                model   : 'labor_gst_free',
                name    : `labor_gst_free_${ this.index }`
              }
            },
            enabled: true,
            factory: TableSwitchControlComponent
          },
          field : 'labor_gst_free',
          header: {
            text: this.translateService.instant('COMPANY_SPACE.TAX_GROUP.LABOUR_FREE')
          }
        },
        {
          component: {
            bindings: {
              outputs: {
                onCancelClick: ( type : TaxGroupItemAttributeModel ) => this.toggleEditing(type, false),
                onEditClick  : ( type : TaxGroupItemAttributeModel ) => this.toggleEditing(type, true),
                onSaveClick  : () => this.save()
              }
            },
            enabled: true,
            factory: TaxGroupActionsComponent,
          },
          sortable: false,
        }
      ],
      empty: {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_TYPES_FOUND')
      },
    };
  }

  addType () : void {
    this.group.tax_group_items_attributes.push({
      tax_type      : '',
      labor_gst_free: false,
      editing       : true
    });
  }

  deleteType ( type : TaxGroupItemAttributeModel ) : void {
    if ( !type.id ) {
      this.group.tax_group_items_attributes = this.group.tax_group_items_attributes.filter(item => item !== type);

      return;
    }

    this.taxGroupsService.deleteType(type)
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.TAX_TYPE'), 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }

  refreshTable () : void {
    this.group.tax_group_items_attributes = this.group.tax_group_items_attributes
      .map(item => ({ ...item }));
  }

  save () : void {
    let isGroupValid = !!this.group.name;

    this.group.tax_group_items_attributes.forEach((item : TaxGroupItemAttributeModel ) => {
      isGroupValid = item.tax_type && item.tax_rate
        ? isGroupValid
        : false;
    });

    if ( isGroupValid ) {
      Promise.resolve()
      .then(() => this.group.id
        ? this.taxGroupsService.update(this.group)
        : this.taxGroupsService.create(this.group))
      .then(() => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.TAX_GROUP'), 'success');

        this.router.stateService.reload();
      })
      .catch(error => this.messagesService.error(error));
    } else {
      this.messagesService.error(this.translateService.instant('JS_SPACE.MESSAGES.INVALID_INPUT'));
    }
  }

  setQboSalesTaxGroup ( group : TaxGroupModel ) : void {
    const match = this.quickbooksTaxRates.find(tax => tax.id === group.qbo_sales_tax_type);

    group.qbo_sales_tax_group_id = match
      ? match.sales_tax_group
      : null;
  }

  toggleEditing ( type : TaxGroupItemAttributeModel, editing : boolean ) : void {
    if ( editing ) {
      if ( type.id && !this.originalTypes.has(type.id) ) {
        this.originalTypes.set(type.id, { ...type, editing: false });
      }

      type.editing = true;
    } else if ( !type.id ) {
      this.deleteType(type);
    } else {
      const originalType = this.originalTypes.get(type.id);

      if ( originalType ) {
        Object.assign(type, originalType);

        this.originalTypes.delete(type.id);
      }

      type.editing = false;
    }

    if ( window[ this.broadcastKey ] ) {
      window[ this.broadcastKey ]();
    }
  }
}
